/**
 * Copyright (c) 2010-2020 California CAD Solutions, Inc. Licensed subject
 * to an End-User License Agreement included in a separate file named EULA.txt
 * that is distributed with this file.
 */
import { inject, Injectable } from '@angular/core';
import { env } from '@env/environment-factory';
import { DashGisUtil } from './dash-gis-util';
import { LoggerService } from './services/logger.service';

@Injectable()
export class LocalStorageProxy implements Storage {
    protected prefix: string;

    protected sessionStorageKeys: string[];
    protected rememberBasedKeys: string[];

    private logger: LoggerService;

    constructor() {
        this.logger = inject(LoggerService);
        this.prefix = env.baseHref.replace(/\//g, '');
        this.sessionStorageKeys = [ 'auth.token', 'auth.needsDisclaimer' ];

        // these are stored in session storage normally, and in local storage when 'Remember Me' is selected
        this.rememberBasedKeys = [ 'auth.refreshToken', 'auth.activeMethod' ];
    }

    key(val: number): string {
        if (localStorage.key(val)) {
            return this.bareKey(localStorage.key(val));
        } else if (sessionStorage.key(val)) {
            return this.bareKey(sessionStorage.key(val));
        }
        return null;
    }

    get length(): number {
        const localStorageLength = Object.keys(localStorage).filter((k) => k.startsWith(`${ this.prefix }_`)).length;
        const sessionStorageLength = Object.keys(sessionStorage).filter((k) => k.startsWith(`${ this.prefix }_`)).length;
        return localStorageLength + sessionStorageLength;
    }

    getItem(key: any): any {
        const rememberString = localStorage.getItem(this.prefixedKey('auth.remember'));
        const remember = DashGisUtil.parseBoolean(rememberString);
        return this.getItemInternal(key, remember);
    }

    setItem(key: string, val: any): void {
        const rememberString = localStorage.getItem(this.prefixedKey('auth.remember'));
        const remember = DashGisUtil.parseBoolean(rememberString);
        this.setItemInternal(key, val, remember);
    }

    removeItem(key: any): void {
        const prefixedKey = this.prefixedKey(key);
        sessionStorage.removeItem(prefixedKey);
        localStorage.removeItem(prefixedKey);
    }

    clear(): void {
        const localKeys = Object.keys(localStorage);
        const sessionKeys = Object.keys(sessionStorage);
        localKeys.forEach((k) => localStorage.removeItem(this.prefixedKey(k)));
        sessionKeys.forEach((k) => sessionStorage.removeItem(this.prefixedKey(k)));
    }

    protected getItemInternal(key: string, remember: boolean): any {
        let maybeItem: any;
        if (this.rememberBasedKeys.includes(key)) {
            if (remember) {
                maybeItem = localStorage.getItem(this.prefixedKey(key));
                sessionStorage.removeItem(this.prefixedKey(key));
            } else {
                maybeItem = sessionStorage.getItem(this.prefixedKey(key));
                localStorage.removeItem(this.prefixedKey(key));
            }
        } else {
            if (this.sessionStorageKeys.includes(key)) {
                maybeItem = sessionStorage.getItem(this.prefixedKey(key));
            } else {
                maybeItem = localStorage.getItem(this.prefixedKey(key));
            }
        }
        return maybeItem;
    }

    protected setItemInternal(key: string, val: any, remember: boolean): void {
        if (this.rememberBasedKeys.includes(key)) {
            if (remember) {
                localStorage.setItem(this.prefixedKey(key), val);
                sessionStorage.removeItem(this.prefixedKey(key));
            } else {
                sessionStorage.setItem(this.prefixedKey(key), val);
                localStorage.removeItem(this.prefixedKey(key));
            }
            return;
        }

        if (this.sessionStorageKeys.includes(key)) {
            sessionStorage.setItem(this.prefixedKey(key), val);
            return;
        }

        localStorage.setItem(this.prefixedKey(key), val);
    }

    protected prefixedKey(key: string): string {
        return `${ this.prefix }_${ key }`;
    }

    private bareKey(key: string): string {
        return key.replace(`${ this.prefix }_`, '');
    }
}
